import { SdkMessageTypes } from '../constants';
import { ITelemetryScenario, ITelemetryService, TelemetryScenarioName, messageToScenarioMap } from '../interfaces';
import { WebContentSdkService } from '../web-content-sdk';
import { IMessageHandler, HandlerType } from './messaging-infra.interface';

type ErrorCallbackType = (error: any, scenario?: ITelemetryScenario) => Promise<unknown>;
type MessageHandlerRegistration = {
  callback: HandlerType;
  scenarioName?: TelemetryScenarioName;
  errorCallback?: ErrorCallbackType;
  isPrivate?: boolean;
};

/**
 * Base Message handler which checks for SDK initialization before executing message requests.
 */
export abstract class BaseMessageHandler implements IMessageHandler {
  protected handlers: {
    [func: string]: MessageHandlerRegistration;
  } = {};

  public appDefinition = this.appSdkHost.getAppDefinition();

  constructor(protected appSdkHost: WebContentSdkService, protected telemetryService?: ITelemetryService) {}

  public registerHandler(
    message: string,
    callback: HandlerType,
    options?: {
      scenarioName?: TelemetryScenarioName;
      errorCallback?: ErrorCallbackType;
      isPrivate?: boolean;
    }
  ): void {
    this.handlers[message] = {
      callback,
      scenarioName: options?.scenarioName ?? messageToScenarioMap[message],
      errorCallback: options?.errorCallback,
      isPrivate: options?.isPrivate,
    };
  }

  public deleteMessageHandling(message: string): void {
    delete this.handlers[message];
  }

  public canHandleMessage(message: string): boolean {
    return this.validateInitialization(message) && this.handlers[message] !== undefined;
  }

  public async handleMessage(message: string, args: unknown[], clientTimestamp?: number): Promise<unknown> {
    if (!this.validateInitialization(message)) {
      return Promise.reject('SDK context is not initialized');
    }
    if (!this.canHandleMessage(message)) {
      return Promise.reject(`Handler cannot handle message: ${message}`);
    }

    const handler = this.handlers[message];

    if (handler.isPrivate && !this.appSdkHost.getAppDefinition()?.isFullTrustApp) {
      return Promise.reject('Private APIs can only be called by Full Trust Apps');
    }

    let scenario: ITelemetryScenario | undefined;
    if (handler.scenarioName && this.telemetryService) {
      scenario = this.telemetryService.createScenario(handler.scenarioName, this.appDefinition?.appId, clientTimestamp);
      if (clientTimestamp) {
        scenario.markPhase('MessageDelay');
      }
    }

    try {
      this.validateDependencies();
      const result = await handler.callback.apply(this, args);
      scenario?.stop();
      return result;
    } catch (error) {
      if (handler.errorCallback) {
        return handler.errorCallback(error, scenario);
      } else {
        scenario?.fail(error);
        return Promise.reject(error);
      }
    }
  }

  private validateInitialization(message: string): boolean {
    return message === SdkMessageTypes.initialize || this.appSdkHost.isInitialized();
  }

  public abstract initHandlers(): void;

  protected validateDependencies(): void {}

  public cleanup(): void {}
}
